import { useExperimentalMutation } from '@confluence/legacy-schema-bridge/entry-points/useExperimentalMutation.ts';
import { MediaType } from '@confluence/graphql/entry-points/MediaType.ts';

import type {
	DraftSyncMutation as DraftSyncMutationResponse,
	DraftSyncMutationVariables,
} from './__types__/DraftSyncMutation.ts';

interface DraftSyncResponse {
	id: string | null;
}

type FlexibleDraftSyncVariables = Omit<DraftSyncMutationVariables, 'ncsStepVersion'> & {
	ncsStepVersion?: string;
};

export const useExperimentalDraftSyncMutation = ({
	onCompleted,
	onError,
}: {
	onCompleted?: (data: DraftSyncMutationResponse) => void;
	onError?: (error: Error) => void;
} = {}) => {
	return useExperimentalMutation<
		DraftSyncMutationResponse,
		FlexibleDraftSyncVariables,
		DraftSyncResponse
	>({
		url: ({ contentId }) => `/wiki/rest/api/content/${contentId}/?status=draft&action=sync`,
		method: 'PUT',
		headers: {
			'Content-Type': MediaType.JSON,
		},
		body: ({
			contentId,
			contentType,
			title,
			content,
			ncsStepVersion,
		}: FlexibleDraftSyncVariables) => {
			const history =
				contentType === 'blogpost' ? { createdDate: new Date().toISOString() } : undefined;
			const extensions = { 'update-trigger': 'DRAFT_SYNC' };

			const body = {
				id: contentId,
				type: contentType,
				title,
				body: {
					atlas_doc_format: {
						value: content,
						representation: 'atlas_doc_format',
						content: {
							id: contentId,
						},
					},
				},
				status: 'draft',
				version: {
					number: 1,
					message: '',
					...(ncsStepVersion ? { ncsStepVersion } : {}),
				},
				extensions,
				history,
			};

			return JSON.stringify(body);
		},
		parseResult: (response) => {
			// The response should match the GraphQL mutation structure
			// experimentalDraftSync: { id: string | null }
			return {
				experimentalDraftSync: {
					id: response?.id || null,
				},
			};
		},
		onCompleted,
		onError,
	});
};
